import React from "react";
import StepHeader from "../components/StepHeader";
import InfoNote from "../components/InfoNote";
import StatusBadge from "../components/StatusBadge";
import ContinueButton from "../components/ContinueButton";
import {
  CLASSIFICATIONS,
  CORPORATE_CLASSES,
  DOCUMENT_CATEGORIES,
  INDIVIDUAL_STEPS,
} from "../content";

/**
 * I-05 / C-05 — the server's classification and what Didit will ask for next.
 * For companies this also lists the required document categories (C-06 / C-07);
 * the documents themselves are uploaded inside Didit.
 */
function ClassificationStep({ application, onStartVerification, onBack, onClose, busy, step, totalSteps }) {
  const isCorporate = application.account_type === "CORPORATE";
  const info = CLASSIFICATIONS[application.classification];

  return (
    <>
      <StepHeader title="Classification" onBack={onBack} onClose={onClose} step={step} totalSteps={totalSteps} />

      <div className="kv-body">
        <section className="kv-card kv-card--highlight kv-result">
          <span className="kv-result__icon" aria-hidden="true">
            <i className={isCorporate ? "ri-building-4-line" : "ri-user-3-line"} />
          </span>
          <div>
            <p className="kv-result__label">{isCorporate ? "Company classification" : "Your classification"}</p>
            <h2 className="kv-result__title">{info?.label}</h2>
          </div>
          <p className="kv-result__reason">{info?.reason(application.answers.business_type)}</p>
        </section>

        {isCorporate ? (
          <CorporateDetails application={application} />
        ) : (
          <IndividualDetails />
        )}
      </div>

      <ContinueButton onClick={onStartVerification} busy={busy}>
        Start verification
      </ContinueButton>
    </>
  );
}

function IndividualDetails() {
  return (
    <>
      <h3 className="kv-section-title">Next: verification steps</h3>
      <ul className="kv-list">
        {INDIVIDUAL_STEPS.map((item) => (
          <li key={item.title} className="kv-card kv-row">
            <span className="kv-row__icon" aria-hidden="true">
              <i className={item.icon} />
            </span>
            <span className="kv-row__text">
              <strong>{item.title}</strong>
              <small>{item.subtitle}</small>
            </span>
            <StatusBadge>Required</StatusBadge>
          </li>
        ))}
      </ul>
      <InfoNote>
        Classification sets your customer category. All individuals complete the same verification steps.
      </InfoNote>
    </>
  );
}

function CorporateDetails({ application }) {
  const required = new Set(application.required_document_categories);

  return (
    <>
      <h3 className="kv-section-title">How corporate classification works</h3>
      <ul className="kv-list">
        {CORPORATE_CLASSES.map((item) => {
          const isYou = item.value === application.classification;
          return (
            <li key={item.value} className={`kv-card kv-row${isYou ? " is-current" : " is-dimmed"}`}>
              <span className="kv-row__text">
                <strong>{CLASSIFICATIONS[item.value].label}</strong>
                <small>{item.rule}</small>
              </span>
              {isYou ? <StatusBadge tone="accent">You</StatusBadge> : null}
            </li>
          );
        })}
      </ul>

      <h3 className="kv-section-title">Required documents</h3>
      <ul className="kv-list">
        {DOCUMENT_CATEGORIES.map((category) => {
          const isRequired = required.has(category.code);
          return (
            <li key={category.code} className={`kv-card kv-row${isRequired ? "" : " is-optional"}`}>
              <span className="kv-row__letter" aria-hidden="true">
                {category.code}
              </span>
              <span className="kv-row__text">
                <strong>{category.title}</strong>
              </span>
              <StatusBadge>{isRequired ? "Required" : "Not required"}</StatusBadge>
            </li>
          );
        })}
      </ul>

      {required.size < DOCUMENT_CATEGORIES.length ? (
        <InfoNote>F and G are not required for Qualified Corporate.</InfoNote>
      ) : null}
      <InfoNote>
        UBO verification is mandatory for every corporate account. You will upload the documents and invite
        your beneficial owners in the next step.
      </InfoNote>
    </>
  );
}

export default ClassificationStep;
